import axios from "axios";
import { clearToken, getToken, getTokenType } from "@/utils/token";
import router from "@/router";
import store from "@/store";
import { Message } from "element-ui";

// 统一请求封装（Vue2 + axios）
// baseURL 建议由环境变量控制，例如：VUE_APP_API_BASE
const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE || "",
  timeout: 120000,
});

function extractErrorMessage(payload) {
  if (!payload) return "";
  if (typeof payload === "string") return payload;
  if (payload.msg) return String(payload.msg);
  if (payload.message) return String(payload.message);
  const detail = payload.detail;
  if (Array.isArray(detail) && detail[0] && detail[0].msg) return String(detail[0].msg);
  return "";
}

function normalizeAuthMessage(msg) {
  console.log("normalizeAuthMessage", msg);
  const s = String(msg || "").trim();
  if (!s) return "";
  // 后端：令牌无效或已过期 -> 前端：更友好的登录提示
  if (s.includes("令牌无效") || s.includes("已过期") || s.toLowerCase().includes("token")) {
    return "登录已过期，请重新登录";
  }
  return s;
}

service.interceptors.request.use((config) => {
  const url = String((config && config.url) || "");
  const headers = (config.headers = config.headers || {});

  const meta = config && config.meta ? config.meta : {};
  const skipToken = meta && meta.isToken === false;

  const token = getToken();
  if (token && !skipToken) {
    const tokenType = getTokenType() || "Bearer";
    headers.Authorization = `${tokenType} ${token}`;
  }

  if (!url) return config;

  // 已包含 /api 或者是绝对地址时，不重复添加
  if (url.includes("/api") || /^https?:\/\//i.test(url)) return config;
  config.url = `/api${url.startsWith("/") ? url : `/${url}`}`;
  return config;
});

// 约定：返回 data（后端返回的 res）
service.interceptors.response.use(
  (response) => {
    // 兼容旧版 utils/request：blob 需要透传 axios response 对象
    if (response && response.config && response.config.responseType === "blob") return response;

    const body = response && (response.data || response);
    const statusRaw = body && (body.resCode ?? body.code);
    const nStatus = statusRaw != null ? Number(statusRaw) : null;
    if (nStatus === 401 || nStatus === 403) {
      // 给出友好提示（避免展示“令牌无效或已过期”等术语）
      const rawMsg = extractErrorMessage(body);
      const friendlyMsg = normalizeAuthMessage(rawMsg) || "登录已过期，请重新登录";
      Message.error(friendlyMsg);
      clearToken();
      if (store && typeof store.commit === "function") {
        try {
          store.commit("clearAuth");
        } catch (e) {
          // ignore
        }
      }
      try {
        const p = router && router.currentRoute ? router.currentRoute.path : "";
        if (p && p.startsWith("/interviewer") && !p.includes("/interviewer/login")) router.push("/");
        else if (p && p.startsWith("/candidate") && !p.includes("/candidate/login")) router.push("/candidate/login");
        else if (router && typeof router.push === "function") router.push("/");
      } catch (e) {
        // ignore
      }
    }
    const bizFailed =
      (body && body.success === false) ||
      (nStatus != null && nStatus !== 200 && nStatus !== 0 && nStatus !== 401 && nStatus !== 403);
    if (bizFailed) {
      const msg = extractErrorMessage(body) || "请求失败，请稍后重试。";
      Message.error(msg);
      const error = new Error(msg);
      error.data = body;
      return Promise.reject(error);
    }
    return body;
  },
  (error) => {
    // 未鉴权：token 过期/失效，清理 token 并跳转登录
    const status = error && error.response ? error.response.status : null;
    if (status === 401 || status === 403) {
      const payload0 = (error && error.response && error.response.data) || error;
      const rawMsg = extractErrorMessage(payload0);
      const friendlyMsg = normalizeAuthMessage(rawMsg) || "登录已过期，请重新登录";
      Message.error(friendlyMsg);
      clearToken();
      if (store && typeof store.commit === "function") {
        try {
          store.commit("clearAuth");
        } catch (e) {
          // ignore
        }
      }
      try {
        const p = router && router.currentRoute ? router.currentRoute.path : "";
        if (p && p.startsWith("/interviewer") && !p.includes("/interviewer/login")) router.push("/");
        else if (p && p.startsWith("/candidate") && !p.includes("/candidate/login")) router.push("/candidate/login");
        else if (router && typeof router.push === "function") router.push("/");
      } catch (e) {
        // ignore
      }
    }
    const payload = (error && error.response && error.response.data) || error;
    const msg = extractErrorMessage(payload) || "请求失败，请稍后重试。";
    // 401/403 已在上面弹过友好提示，这里避免重复弹窗
    if (!(status === 401 || status === 403)) {
      Message.error(msg);
    }
    if (error && typeof error === "object" && !error.data && payload) {
      error.data = payload;
    }
    return Promise.reject(error);
  }
);

export default service;

// 常用封装：保持业务层写法简洁
export const get = (url, params, config = {}) => service.get(url, { params, ...config });
export const post = (url, params, config = {}) => service.post(url, params, config);
export const del = (url, params, config = {}) => service.delete(url, { params, ...config });
export const put = (url, params, config = {}) => service.put(url, params, config);

